import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { safeOnSnapshot } from '../../../utils/firestoreSubscriptions';
import { db } from '../../../firebase-client';
import { useAuth } from '../../../auth/AuthContext';

const DEFAULT_PREFERENCES = {
  hidden: [],
  order: [],
};

function normalizeIds(value) {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value
        .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
        .filter(Boolean)
    )
  );
}

function normalizePreferences(value) {
  if (!value || typeof value !== 'object') return DEFAULT_PREFERENCES;
  return {
    hidden: normalizeIds(value.hidden),
    order: normalizeIds(value.order),
  };
}

function mergeAvailableIds(order = [], availableIds = []) {
  const availableSet = new Set(availableIds);
  const next = normalizeIds(order).filter((id) => availableSet.has(id));
  availableIds.forEach((id) => {
    if (!next.includes(id)) next.push(id);
  });
  return next;
}

export function useTeamCardPreferences(teamId) {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const preferencesRef = useRef(DEFAULT_PREFERENCES);

  useEffect(() => {
    preferencesRef.current = preferences;
  }, [preferences]);

  useEffect(() => {
    if (!user?.uid || !teamId) {
      setPreferences(DEFAULT_PREFERENCES);
      setLoading(false);
      return undefined;
    }

    const settingsRef = doc(db, 'users', user.uid, 'teamSettings', teamId);
    setLoading(true);

    const unsubscribe = safeOnSnapshot(
      settingsRef,
      (snap) => {
        const nextPreferences = normalizePreferences(
          snap.exists() ? snap.data()?.uiPreferences?.teamDetailCards : null
        );
        setPreferences(nextPreferences);
        setLoading(false);
      },
      () => {
        setPreferences(DEFAULT_PREFERENCES);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [teamId, user?.uid]);

  const persist = useCallback(
    async (nextPreferences) => {
      if (!user?.uid || !teamId) {
        return { ok: false, error: new Error('Missing user or team id.') };
      }

      const settingsRef = doc(db, 'users', user.uid, 'teamSettings', teamId);
      const previous = preferencesRef.current;
      const normalized = normalizePreferences(nextPreferences);

      setSaving(true);
      setPreferences(normalized);

      try {
        await setDoc(
          settingsRef,
          {
            uiPreferences: {
              teamDetailCards: normalized,
            },
          },
          { merge: true }
        );
        return { ok: true, preferences: normalized };
      } catch (error) {
        setPreferences(previous);
        return { ok: false, error };
      } finally {
        setSaving(false);
      }
    },
    [teamId, user?.uid]
  );

  const setCardHidden = useCallback(
    async (cardId, shouldHide) => {
      const normalizedId = typeof cardId === 'string' ? cardId.trim() : '';
      if (!normalizedId) {
        return { ok: false, error: new Error('Invalid card id.') };
      }

      const previous = preferencesRef.current;
      const nextHidden = shouldHide
        ? Array.from(new Set([...previous.hidden, normalizedId]))
        : previous.hidden.filter((id) => id !== normalizedId);

      return persist({
        ...previous,
        hidden: nextHidden,
      });
    },
    [persist]
  );

  const showAllCards = useCallback(async () => {
    const previous = preferencesRef.current;
    return persist({
      ...previous,
      hidden: [],
    });
  }, [persist]);

  const moveCard = useCallback(
    async (cardId, direction, availableIds = []) => {
      const normalizedId = typeof cardId === 'string' ? cardId.trim() : '';
      if (!normalizedId) {
        return { ok: false, error: new Error('Invalid card id.') };
      }

      const previous = preferencesRef.current;
      const orderedIds = mergeAvailableIds(previous.order, normalizeIds(availableIds));
      const currentIndex = orderedIds.indexOf(normalizedId);
      if (currentIndex === -1) {
        return { ok: false, error: new Error('Card is not available to reorder.') };
      }

      const delta = direction < 0 ? -1 : 1;
      const nextIndex = currentIndex + delta;
      if (nextIndex < 0 || nextIndex >= orderedIds.length) {
        return { ok: true, preferences: previous };
      }

      const nextOrder = [...orderedIds];
      [nextOrder[currentIndex], nextOrder[nextIndex]] = [nextOrder[nextIndex], nextOrder[currentIndex]];

      return persist({
        ...previous,
        order: nextOrder,
      });
    },
    [persist]
  );

  const resetPreferences = useCallback(async () => persist(DEFAULT_PREFERENCES), [persist]);

  return useMemo(
    () => ({
      preferences,
      loading,
      saving,
      setCardHidden,
      showAllCards,
      moveCard,
      resetPreferences,
    }),
    [loading, moveCard, preferences, resetPreferences, saving, setCardHidden, showAllCards]
  );
}

export function mergeCardOrder(preferredOrder = [], availableIds = []) {
  return mergeAvailableIds(preferredOrder, normalizeIds(availableIds));
}

export function placeUnpositionedCardAfter(orderedIds = [], preferredOrder = [], cardId = '', anchorId = '') {
  if (!cardId || !anchorId || normalizeIds(preferredOrder).includes(cardId)) return orderedIds;
  const next = normalizeIds(orderedIds).filter(id => id !== cardId);
  const anchorIndex = next.indexOf(anchorId);
  if (anchorIndex < 0) return [...next, cardId];
  next.splice(anchorIndex + 1, 0, cardId);
  return next;
}

export function placeCardAfter(orderedIds = [], cardId = '', anchorId = '') {
  if (!cardId || !anchorId) return normalizeIds(orderedIds);
  const next = normalizeIds(orderedIds);
  if (!next.includes(cardId) || !next.includes(anchorId)) return next;
  const withoutCard = next.filter((id) => id !== cardId);
  withoutCard.splice(withoutCard.indexOf(anchorId) + 1, 0, cardId);
  return withoutCard;
}
